import React, { useState, useMemo } from 'react';
import { Accordion, AccordionItem, Chip, Button } from '@nextui-org/react';
import { FullScreenModal } from './FullScreenModal';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { RendererHeader } from './RendererHeader';
import { ScoreCard } from './ScoreCard';
import { RendererTabs } from './RendererTabs';
import { MarkdownContent } from './AISummaryAccordion';
import { parseScore } from '../utils/dateutils';
import { ErrorDisplay } from './ErrorDisplay';
import { formatKeyForDisplay } from '../utils/commonutils';
import { AttachmentByKeyBlock, AttachmentUrlProvider, isAttachmentFieldKey } from './claimScoreAttachmentMedia';
import { useScoreColor } from './claimScoreRendererShared';

export interface ClaimDataRendererProps {
    claimData: any;
    onButtonAction?: (action: string, buttonData: any) => void;
    showActionButtons?: boolean;
    className?: string;
    isModal?: boolean;
    feedbackComponent?: React.ReactNode;
    knowledgeComponent?: React.ReactNode;
    onRefresh?: () => void;
    /** When provided, "Full View" delegates to this instead of the built-in modal (e.g. widget host opening a real fullscreen tab). */
    onRequestFullScreen?: () => void;
}

const TITLE_LABEL_CLASS = 'text-[#667085] text-[14px] font-semibold';

const NESTED_ACCORDION_ITEM_CLASSNAMES = {
    content: 'pl-0 pr-0 !py-0',
    base: 'w-full !border-none !shadow-none !bg-transparent !px-0',
    title: 'text-base w-full',
    trigger: 'group py-1.5 !px-0 !m-0 !gap-2',
} as const;

const NESTED_ACCORDION_ITEM_CLASSNAME = '[&>h2>button]:block';

const EMPTY_INDICATOR = <></>;
const SMALL_OBJECT_FIELD_LIMIT = 6;
const EMPTY_VALUE_PLACEHOLDER = <div className="py-1.5 text-sm text-default-500">—</div>;

const FILTERED_ACTION_KEYS = ['_json', 'current_claim_object', 'is_authorized', 'pre_condition_metric', 'adjusted_condition_overall', 'recipient_details','claim intake object', 'source_ids', 'source ids'];

const HIDDEN_FIELD_KEYS = new Set<string>(['finding_number','pdf_source_ids','source_ids','item_number','file_name','source_id','type','doc_id','presigned_url','filename']);

const hasValue = (v: any) => v !== undefined && v !== null;

const isPlainObject = (v: any): v is Record<string, any> =>
    v !== null && typeof v === 'object' && !Array.isArray(v);

function getVisibleFieldCount(obj: Record<string, any>): number {
    return Object.keys(obj).filter(k => k !== 'score').length;
}

type EntryBucket = 'skip' | 'primitive' | 'inline' | 'accordion';

function categorizeEntry(k: string, v: any, hasObjectScore: boolean): { bucket: EntryBucket; value: any } {
    if (HIDDEN_FIELD_KEYS.has(k)) return { bucket: 'skip', value: v };
    if (k === 'score' && hasObjectScore) return { bucket: 'skip', value: v };
    if (Array.isArray(v) && v.length === 0) return { bucket: 'primitive', value: null };
    if (v === null || typeof v !== 'object') return { bucket: 'primitive', value: v };
    if (isPlainObject(v) && getVisibleFieldCount(v) < SMALL_OBJECT_FIELD_LIMIT) return { bucket: 'inline', value: v };
    return { bucket: 'accordion', value: v };
}

/** Score chip whose color follows the agent-config thresholds (falls back to 85/60 defaults). */
const ScoreChip: React.FC<{ score: any }> = ({ score }) => {
    const getScoreColor = useScoreColor();
    const numScore = Math.round(parseScore(score));
    if (Number.isNaN(numScore)) return null;
    const color = getScoreColor(numScore);
    if (color === 'warning') {
        return (
            <Chip variant="flat" size="sm" style={{ backgroundColor: '#FEF3C7', color: '#F59E0B' }}>
                {`${numScore}%`}
            </Chip>
        );
    }
    return (
        <Chip color={color} variant="flat" size="sm">
            {`${numScore}%`}
        </Chip>
    );
};

function renderAccordionTitle(label: React.ReactNode, trailing?: React.ReactNode | null) {
    return (
        <div className="flex items-center w-full gap-2">
            <div className="flex items-center gap-1 whitespace-nowrap shrink-0">
                <span className={TITLE_LABEL_CLASS}>{label}</span>
                <Icon
                    icon="mdi:chevron-down"
                    className="text-[#667085] w-4 h-4 transition-transform group-aria-expanded:rotate-180"
                />
            </div>
            <div className="flex-1 border-b border-default-300 min-w-[8px]" />
            {trailing ?? null}
        </div>
    );
}

function primitiveToString(value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value) && value.length === 0) return '—';
    if (typeof value === 'boolean') return value ? 'True' : 'False';
    if (typeof value === 'object') return JSON.stringify(value) ?? '—';
    return String(value);
}

const INLINE_VALUE_LENGTH_LIMIT = 50;

function isFiniteNumericValue(value: any): boolean {
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value !== 'string') return false;
    if (value.trim() === '') return false;
    return Number.isFinite(Number(value));
}

function renderPrimitiveRow(key: string | undefined, value: any, rowKey?: string) {
    const strValue = primitiveToString(value);
    const rowId = rowKey ?? key ?? 'value';

    if (key && key.toLowerCase().includes('score') && isFiniteNumericValue(value)) {
        return (
            <div key={rowId} className="flex items-center justify-between gap-2 py-1.5 min-w-0">
                <span className="renderer-field-name capitalize font-medium text-default-700 min-w-[140px] shrink-0">
                    {formatKeyForDisplay(key)}
                </span>
                <span className="shrink-0"><ScoreChip score={value} /></span>
            </div>
        );
    }

    const useInline = !key || strValue.length < INLINE_VALUE_LENGTH_LIMIT;

    if (useInline) {
        return (
            <div key={rowId} className="flex items-start justify-between gap-2 py-1.5 min-w-0">
                {key && (
                    <span className="renderer-field-name capitalize font-medium text-default-700 min-w-[140px] shrink-0">
                        {formatKeyForDisplay(key)}
                    </span>
                )}
                <span className="renderer-field-value text-default-500 text-right break-words min-w-0 overflow-x-auto max-w-[65%]">
                    {strValue}
                </span>
            </div>
        );
    }

    return (
        <div key={rowId} className="flex flex-col gap-1 py-1.5 min-w-0">
            <span className="renderer-field-name capitalize font-medium text-default-700">
                {formatKeyForDisplay(key ?? '')}
            </span>
            <span className="renderer-field-value text-default-500 break-words whitespace-pre-line min-w-0">
                {strValue}
            </span>
        </div>
    );
}

function renderNestedAccordion(items: React.ReactElement[]) {
    return (
        <Accordion
            variant="light"
            isCompact
            selectionMode="multiple"
            className="[&_hr]:hidden [&_.divider]:hidden [&_[role=separator]]:hidden !px-0 !p-0 !m-0"
            dividerProps={{ className: 'hidden' }}
        >
            {items}
        </Accordion>
    );
}

interface NestedAccordionItemOpts {
    itemKey: string;
    label: React.ReactNode;
    ariaLabel: string;
    score?: any;
    children: React.ReactNode;
}

function buildNestedAccordionItem({
    itemKey, label, ariaLabel, score, children,
}: NestedAccordionItemOpts): React.ReactElement {
    return (
        <AccordionItem
            key={itemKey}
            aria-label={ariaLabel}
            title={renderAccordionTitle(label, hasValue(score) ? <ScoreChip score={score} /> : null)}
            indicator={EMPTY_INDICATOR}
            classNames={NESTED_ACCORDION_ITEM_CLASSNAMES}
            className={NESTED_ACCORDION_ITEM_CLASSNAME}
        >
            {children}
        </AccordionItem>
    );
}

function buildAccordionItems(actions: Record<string, any>) {
    return Object.entries(actions)
        .filter(([key]) => !FILTERED_ACTION_KEYS.some(fk => key.toLowerCase().includes(fk)))
        .map(([key, value]: [string, any]) => {
            const hasError = value && typeof value === 'object' && (value.error || value.body);
            let errorData: any = null;
            if (hasError) {
                errorData = { error: value.error, body: value.body };
                if (value.body) {
                    if (typeof value.body === 'string') {
                        try { errorData.parsedBody = JSON.parse(value.body); } catch { /* ignore */ }
                    } else if (typeof value.body === 'object') {
                        errorData.parsedBody = value.body;
                    }
                }
            }
            return {
                key: key.toLowerCase().replace(/\s+/g, '_'),
                title: key,
                data: value,
                score: typeof value === 'object' && value !== null ? value.score : undefined,
                error: errorData,
            };
        });
}

function SingleClaimRenderer({
    claimData,
    onButtonAction,
    showActionButtons = true,
    className = '',
    isModal = false,
    feedbackComponent,
    knowledgeComponent,
    onRefresh,
    onRequestFullScreen
}: Readonly<ClaimDataRendererProps>) {
    const { t } = useTranslation();
    // Agent-config driven score color (falls back to 85/60 defaults when no provider/config).
    const getScoreColor = useScoreColor();
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('overview');
    const [isExpanded, setIsExpanded] = useState(false);

    const agentName = claimData?.agent_name || 'Unknown Agent';
    const entityNumber = claimData?.entity_number || 'Unknown';
    const aiSummary = claimData?.ai_summary || '';
    const agentScore = claimData?.agent_score || '';
    const actionButtons = claimData?.action_button || [];
    const timestamp = claimData?.timestamp || '';
    const duration = claimData?.duration_ms ?? 0;
    const actions = claimData?.action_output || {};

    const accordionItems = useMemo(() => buildAccordionItems(actions), [actions]);

    const scoreValue = parseScore(agentScore);
    const scoreColor = getScoreColor(scoreValue);
    const displayScore = agentScore && agentScore !== 'N/A' && agentScore !== ''
        ? `${Math.round(scoreValue)}%`
        : null;

    const matchBreakdown = useMemo(() => {
        const breakdown = { matches: 0, partial: 0, mismatches: 0 };
        for (const item of accordionItems) {
            if (!hasValue(item.score)) continue;
            const s = Math.round(parseScore(item.score));
            if (s >= 85) breakdown.matches++;
            else if (s >= 60) breakdown.partial++;
            else breakdown.mismatches++;
        }
        return breakdown;
    }, [accordionItems]);

    const allKeys = useMemo(() => {
        const keys = new Set<string>();
        if (aiSummary) keys.add('ai-summary');
        for (const item of accordionItems) keys.add(item.key);
        return keys;
    }, [aiSummary, accordionItems]);

    if (!claimData) {
        return (
            <div className={`flex items-center justify-center py-12 ${className}`}>
                <div className="text-default-500">{t('No claim data available')}</div>
            </div>
        );
    }

    const handleExpandAll = () => {
        if (isExpanded) {
            setSelectedKeys(new Set());
            setIsExpanded(false);
        } else {
            setSelectedKeys(allKeys);
            setIsExpanded(true);
        }
    };

    function renderValueBody(value: any, itemKey: string, arrayKey: string): React.ReactNode {
        if (Array.isArray(value)) return renderArrayValue(arrayKey, value, itemKey);
        if (isPlainObject(value)) return renderObjectEntries(value, itemKey);
        return renderPrimitiveRow(undefined, value, `${itemKey}-val`);
    }

    function renderFlowEntries(items: Record<string, any>[], parentPath: string): React.ReactNode {
        const nodes: React.ReactNode[] = [];
        let primitiveRun: [string, any][] = [];
        const flushPrimitiveRun = () => {
            if (primitiveRun.length === 0) return;
            const rows = primitiveRun;
            primitiveRun = [];
            nodes.push(
                <div key={`flow-prim-${parentPath}-${nodes.length}`} className="space-y-0">
                    {rows.map(([k, v], i) => renderPrimitiveRow(k, v, `${parentPath}-flow-${k}-${i}`))}
                </div>,
            );
        };
        items.forEach((item, idx) => {
            const [k, v] = Object.entries(item)[0] ?? [];
            if (k === undefined) return;
            if (isAttachmentFieldKey(k)) {
                flushPrimitiveRun();
                nodes.push(<AttachmentByKeyBlock key={`flow-att-${parentPath}-${idx}`} fieldKey={k} value={v} />);
                return;
            }
            const { bucket, value } = categorizeEntry(k, v, false);
            if (bucket === 'skip') return;
            if (bucket === 'primitive') {
                primitiveRun.push([k, value]);
                return;
            }
            flushPrimitiveRun();
            if (bucket === 'inline') {
                nodes.push(
                    <React.Fragment key={`flow-inline-${parentPath}-${idx}`}>
                        {renderCollectionInline(k, value, `${parentPath}-flow-${idx}`)}
                    </React.Fragment>,
                );
            } else {
                nodes.push(
                    <React.Fragment key={`flow-acc-${parentPath}-${idx}`}>
                        {renderNestedAccordion([renderCollectionAccordionItem(k, value, `${parentPath}-flow-${idx}`)])}
                    </React.Fragment>,
                );
            }
        });
        flushPrimitiveRun();
        return <div className="space-y-2">{nodes}</div>;
    }

    function renderArrayValue(arrayKey: string, arr: any[], parentPath: string): React.ReactNode {
        if (arr.length === 0) return EMPTY_VALUE_PLACEHOLDER;
        if (arr.every((it: any) => isPlainObject(it) && Object.keys(it).length === 1)) {
            return renderFlowEntries(arr as Record<string, any>[], parentPath);
        }
        const formattedKey = formatKeyForDisplay(arrayKey) || t('Item');
        const hasComplex = arr.some((it: any) => isPlainObject(it) || Array.isArray(it));
        if (!hasComplex) {
            return renderPrimitiveRow(
                arrayKey,
                arr.map((item: any) => primitiveToString(item)).join(', '),
                `${parentPath}-${arrayKey}`,
            );
        }

        const allSmallPrimitiveObjects = arr.every((it: any) =>
            isPlainObject(it)
            && getVisibleFieldCount(it) < SMALL_OBJECT_FIELD_LIMIT
            && Object.values(it).every(v => v === null || typeof v !== 'object'),
        );

        if (allSmallPrimitiveObjects) {
            return (
                <div className="space-y-2">
                    {arr.map((item: any, idx: number) => {
                        const itemKey = `${parentPath}-${arrayKey}-${idx}`;
                        const title = `${formattedKey} #${idx + 1}`;
                        const score = item.score;
                        return (
                            <div key={itemKey} className="pt-1">
                                <div className="flex items-center w-full gap-2 mb-1">
                                    <span className={TITLE_LABEL_CLASS}>{title}</span>
                                    <div className="flex-1 border-b border-default-200" />
                                    {hasValue(score) ? <ScoreChip score={score} /> : null}
                                </div>
                                <div className="pl-2">{renderObjectEntries(item, itemKey)}</div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        return renderNestedAccordion(
            arr.map((item: any, idx: number) => {
                const itemKey = `${parentPath}-${arrayKey}-${idx}`;
                const title = `${formattedKey} #${idx + 1}`;
                return buildNestedAccordionItem({
                    itemKey,
                    label: title,
                    ariaLabel: title,
                    score: isPlainObject(item) ? item.score : undefined,
                    children: <div className="pl-2">{renderValueBody(item, itemKey, title)}</div>,
                });
            }),
        );
    }

    function renderCollectionAccordionItem(key: string, value: any, parentPath: string): React.ReactElement {
        const formattedKey = formatKeyForDisplay(key);
        const itemKey = `${parentPath}-${key}`;
        return buildNestedAccordionItem({
            itemKey,
            label: formattedKey,
            ariaLabel: formattedKey,
            score: isPlainObject(value) ? value.score : undefined,
            children: <div className="pl-2">{renderValueBody(value, itemKey, key)}</div>,
        });
    }

    function renderCollectionInline(key: string, value: any, parentPath: string): React.ReactNode {
        const formattedKey = formatKeyForDisplay(key);
        const itemKey = `${parentPath}-${key}`;
        const score = isPlainObject(value) ? value.score : undefined;
        return (
            <div key={itemKey} className="pt-1">
                <div className="flex items-center w-full gap-2 mb-1">
                    <span className={TITLE_LABEL_CLASS}>{formattedKey}</span>
                    <div className="flex-1" />
                    {hasValue(score) ? <ScoreChip score={score} /> : null}
                </div>
                <div className="pl-2">{renderValueBody(value, itemKey, key)}</div>
            </div>
        );
    }

    function renderObjectEntries(obj: Record<string, any>, parentPath: string): React.ReactNode {
        const entries = Object.entries(obj);
        if (entries.length === 0) return EMPTY_VALUE_PLACEHOLDER;

        const hasObjectScore = hasValue(obj.score);
        const primitives: [string, any][] = [];
        const inlineCollections: [string, any][] = [];
        const accordionCollections: [string, any][] = [];
        const buckets = { primitive: primitives, inline: inlineCollections, accordion: accordionCollections };

        for (const [k, v] of entries) {
            const { bucket, value } = categorizeEntry(k, v, hasObjectScore);
            if (bucket === 'skip') continue;
            buckets[bucket].push([k, value]);
        }

        return (
            <div className="space-y-2">
                {primitives.length > 0 && (
                    <div className="space-y-0">
                        {primitives.map(([k, v]) => renderPrimitiveRow(k, v, `${parentPath}-${k}`))}
                    </div>
                )}
                {inlineCollections.map(([k, v]) => renderCollectionInline(k, v, parentPath))}
                {accordionCollections.length > 0 && renderNestedAccordion(
                    accordionCollections.map(([k, v]) => renderCollectionAccordionItem(k, v, parentPath)),
                )}
            </div>
        );
    }

    function renderJsonNode(value: any, parentKey: string, parentPath: string): React.ReactNode {
        if (value === null || value === undefined) return EMPTY_VALUE_PLACEHOLDER;
        if (Array.isArray(value)) return renderArrayValue(parentKey, value, parentPath);
        if (typeof value === 'object') return renderObjectEntries(value, parentPath);
        return renderPrimitiveRow(undefined, value, parentPath);
    }

    const renderAccordionItemContent = (item: { key: string; title: string; error: any; data: any }) => {
        if (item.error) return <ErrorDisplay errorData={item.error} />;
        if (item.data !== undefined && item.data !== null) {
            return (
                <div className="rounded-md border border-default-200 p-3 bg-content1 overflow-hidden">
                    {renderJsonNode(item.data, item.title, item.key)}
                </div>
            );
        }
        return <div className="text-default-500 text-sm">{t('No data available')}</div>;
    };

    const overviewContent = (
        <Accordion
            variant="splitted"
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={(keys) => setSelectedKeys(keys as Set<string>)}
            isCompact
            className="gap-2 [&_hr]:hidden [&_.divider]:hidden"
        >
            {aiSummary && (
                <AccordionItem
                    key="ai-summary"
                    aria-label={t('AI Summary')}
                    title={
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-pink-100 rounded-lg">
                                <Icon icon="mdi:sparkles" className="text-pink-600" style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                            </div>
                            <div>
                                <div className="renderer-text-primary font-semibold">{t('AI Summary')}</div>
                                <div className="renderer-text-secondary">{t('AI-generated analysis overview')}</div>
                            </div>
                        </div>
                    }
                    className="mb-3"
                    classNames={{
                        content: 'pl-1 pr-0',
                        base: 'w-full !px-2 !shadow-none border-2',
                        title: 'text-base',
                    }}
                >
                    <div className="pt-1">
                        <MarkdownContent content={aiSummary} />
                    </div>
                </AccordionItem>
            )}

            {accordionItems.length > 0 ? (
                accordionItems.map((item) => buildNestedAccordionItem({
                    itemKey: item.key,
                    ariaLabel: formatKeyForDisplay(item.title),
                    label: (
                        <span className={item.error ? 'text-red-600' : undefined}>
                            {item.error && <Icon icon="mdi:alert-circle" className="w-4 h-4 text-red-600 inline mr-1" />}
                            {formatKeyForDisplay(item.title)}
                        </span>
                    ),
                    score: item.score,
                    children: <div className="pt-2">{renderAccordionItemContent(item)}</div>,
                }))
            ) : (
                !aiSummary && (
                    <AccordionItem
                        key="no-details"
                        aria-label={t('No Details')}
                        title={
                            <div className="flex items-center gap-3">
                                <Icon icon="mdi:information-outline" className="w-5 h-5 text-default-500" />
                                <span className="font-medium text-default-500">{t('No Details Available')}</span>
                            </div>
                        }
                        className="mb-3"
                    >
                        <div className="text-center py-8 text-default-500">
                            <Icon icon="mdi:information-outline" className="w-8 h-8 mx-auto mb-2 text-default-400" />
                            <p className="text-sm">{t('No details available')}</p>
                        </div>
                    </AccordionItem>
                )
            )}
        </Accordion>
    );

    return (
        <AttachmentUrlProvider data={actions}>
            <div className={`flex flex-col h-full ${className}`} style={{ '--accordion-divider-display': 'none' } as React.CSSProperties}>
                <div className="border-b-3 border-default-200 bg-default-100 m-2 rounded-md">
                    <RendererHeader
                        agentName={agentName}
                        duration={duration}
                        entityNumber={entityNumber}
                        timestamp={timestamp}
                        isModal={isModal}
                        onFullScreenClick={() => onRequestFullScreen ? onRequestFullScreen() : setIsFullScreenOpen(true)}
                        onRefresh={onRefresh}
                    />

                    {displayScore && (
                        <div className="flex-none pl-3 pr-3">
                            <ScoreCard
                                displayScore={displayScore}
                                scoreValue={scoreValue}
                                scoreColor={scoreColor}
                                matchBreakdown={matchBreakdown}
                                activeFilter={null}
                                onFilterClick={() => {}}
                                feedbackComponent={feedbackComponent}
                            />
                        </div>
                    )}

                    {!displayScore && feedbackComponent && (
                        <div className="px-3 pb-3">
                            {feedbackComponent}
                        </div>
                    )}
                </div>

                <RendererTabs
                    activeTab={activeTab}
                    onTabChange={(key) => setActiveTab(key)}
                    isExpanded={isExpanded}
                    onExpandAll={handleExpandAll}
                    knowledgeComponent={knowledgeComponent}
                    overviewContent={overviewContent}
                />

                {showActionButtons && (
                    <div className="flex-none p-3 border-t border-default-200">
                        {actionButtons && actionButtons.length > 0 ? (
                            <div className="flex gap-3 justify-center">
                                {actionButtons.map((button: any) => (
                                    <CtyButton
                                        key={button.id}
                                        color={button.color}
                                        variant="solid"
                                        className="flex-1"
                                        onPress={() => onButtonAction?.(button.action, claimData)}
                                    >
                                        {button.label}
                                    </CtyButton>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-default-500 text-sm">
                                {t('No actions available')}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!isModal && (
                <FullScreenModal
                    isOpen={isFullScreenOpen}
                    onClose={() => setIsFullScreenOpen(false)}
                    agentName={agentName}
                    modalClassName="claim-data-renderer"
                    headerBorder
                >
                    <SingleClaimRenderer
                        claimData={claimData}
                        onButtonAction={onButtonAction}
                        showActionButtons={showActionButtons}
                        knowledgeComponent={knowledgeComponent}
                        feedbackComponent={feedbackComponent}
                        isModal={true}
                    />
                </FullScreenModal>
            )}
        </AttachmentUrlProvider>
    );
}

function MultiClaimRenderer(props: Readonly<ClaimDataRendererProps> & { claimDataArray: any[] }) {
    const { t } = useTranslation();
    const { claimDataArray, onButtonAction, showActionButtons, className = '', isModal, feedbackComponent, knowledgeComponent, onRefresh, onRequestFullScreen } = props;
    const [selectedClaimKeys, setSelectedClaimKeys] = useState<Set<string>>(new Set());

    const allClaimKeys = useMemo(() => {
        const keys = new Set<string>();
        claimDataArray.forEach((c, i) => {
            const claimId = c?.entity_number || c?.claim_no || `claim-${i}`;
            keys.add(`claim-${claimId}-${i}`);
        });
        return keys;
    }, [claimDataArray]);

    const isAllClaimsExpanded = selectedClaimKeys.size === allClaimKeys.size && allClaimKeys.size > 0;

    const handleToggleAllClaims = () => {
        setSelectedClaimKeys(isAllClaimsExpanded ? new Set() : allClaimKeys);
    };

    return (
        <div className={`flex flex-col h-full ${className}`}>
            <div className="flex-none p-4 border-b border-default-200 bg-content1">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-foreground">
                        {t('Claim Information')} ({claimDataArray.length} {t('claims')})
                    </h2>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="flat"
                            color={isAllClaimsExpanded ? 'default' : 'primary'}
                            startContent={<Icon icon={isAllClaimsExpanded ? 'mdi:collapse-all' : 'mdi:expand-all'} className="w-4 h-4" />}
                            onClick={handleToggleAllClaims}
                            className="text-xs"
                        >
                            {isAllClaimsExpanded ? t('Collapse All') : t('Expand All')}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
                <Accordion
                    variant="splitted"
                    selectionMode="multiple"
                    selectedKeys={selectedClaimKeys}
                    onSelectionChange={(keys) => setSelectedClaimKeys(keys as Set<string>)}
                >
                    {claimDataArray.map((claim: any, index: number) => {
                        const claimTitle = claim.entity_number || claim.claim_no || `${t('Claim')} ${index + 1}`;
                        const claimAgentName = claim.agent_name || 'Unknown Agent';
                        const claimScore = claim.agent_score;
                        const hasClaimScore = claimScore && claimScore !== 'N/A';
                        const claimKey = `claim-${claim.entity_number || claim.claim_no || index}-${index}`;

                        return buildNestedAccordionItem({
                            itemKey: claimKey,
                            ariaLabel: claimTitle,
                            label: (
                                <span>
                                    <span className="block">{claimTitle}</span>
                                    <span className="text-xs text-default-500 font-normal">{claimAgentName}</span>
                                </span>
                            ),
                            score: hasClaimScore ? claimScore : undefined,
                            children: (
                                <SingleClaimRenderer
                                    claimData={claim}
                                    onButtonAction={onButtonAction}
                                    showActionButtons={showActionButtons}
                                    className="border-none"
                                    isModal={isModal}
                                    feedbackComponent={feedbackComponent}
                                    knowledgeComponent={knowledgeComponent}
                                    onRefresh={onRefresh}
                                    onRequestFullScreen={onRequestFullScreen}
                                />
                            ),
                        });
                    })}
                </Accordion>
            </div>
        </div>
    );
}

function buildLegacyClaimData(claimData: any) {
    const outputData = claimData?.output_data || {};
    const requests = outputData.requests || [];
    return {
        agent_name: claimData?.aiworker_name || 'Unknown Agent',
        entity_number: claimData?.metadata?.claim_object?.claim_no || 'Unknown',
        ai_summary: outputData.summary || '',
        agent_score: 100,
        action_button: [] as any[],
        action_output: requests[0]?.actions || {},
        timestamp: claimData?.used_at || 'recently',
    };
}

export function ClaimDataRenderer({
    claimData,
    onButtonAction,
    showActionButtons = true,
    className = '',
    isModal = false,
    feedbackComponent,
    knowledgeComponent,
    onRefresh,
    onRequestFullScreen
}: Readonly<ClaimDataRendererProps>) {
    const { t } = useTranslation();

    const renderEmpty = () => (
        <div className={`flex items-center justify-center py-12 ${className}`}>
            <div className="text-default-500">{t('No claim data available')}</div>
        </div>
    );

    if (!claimData) return renderEmpty();

    const passthrough = {
        onButtonAction,
        showActionButtons,
        className,
        isModal,
        feedbackComponent,
        knowledgeComponent,
        onRefresh,
        onRequestFullScreen,
    };

    const isClaimArray = Array.isArray(claimData) && claimData.length > 0;

    if (isClaimArray && claimData.length > 1) {
        return (
            <MultiClaimRenderer
                claimData={claimData}
                claimDataArray={claimData}
                {...passthrough}
            />
        );
    }

    if (isClaimArray) {
        return <SingleClaimRenderer claimData={claimData[0]} {...passthrough} />;
    }

    const legacyClaimData = buildLegacyClaimData(claimData);
    const hasLegacyData = legacyClaimData.ai_summary || Object.keys(legacyClaimData.action_output).length > 0;

    if (!hasLegacyData) return renderEmpty();

    return <SingleClaimRenderer claimData={legacyClaimData} {...passthrough} />;
}

export default ClaimDataRenderer;
